// ===== SERVIÇO DE INTEGRAÇÃO GEMINI 2.5 FLASH LITE =====
// Processa perguntas do professor usando Gemini AI

import { GoogleGenerativeAI } from "@google/generative-ai";
import { GeminiContext } from "../types";

export class GeminiService {
  private genAI: GoogleGenerativeAI;
  private model: any;

  constructor(apiKey: string) {
    this.genAI = new GoogleGenerativeAI(apiKey);
    // Usando Gemini 2.5 Flash Lite (modelo otimizado e rápido)
    this.model = this.genAI.getGenerativeModel({
      model: "gemini-2.5-flash-lite",
      generationConfig: {
        temperature: 0.7,
        topP: 0.95,
        topK: 40,
        maxOutputTokens: 2048,
      },
    });
  }

  // ===== CONSTRUIR PROMPT DO SISTEMA =====
  private buildSystemPrompt(context: GeminiContext): string {
    let systemPrompt = `Você é o Assistente SIGA, um tutor inteligente especializado em ajudar professores de matemática do Ensino Fundamental e Médio (BNCC).

SEU PAPEL:
- Analisar dados de desempenho de alunos e turmas
- Dar sugestões pedagógicas práticas e acionáveis
- Criar planos de aula e atividades personalizadas
- Analisar documentos (provas, exercícios, planos de aula)
- Responder de forma clara, concisa e empática

INSTRUÇÕES:
- Use emoji moderadamente (apenas para destacar pontos importantes)
- Seja objetivo e direto nas respostas
- Sempre baseie recomendações nos dados fornecidos
- Quando não tiver dados suficientes, pergunte o que precisa
- Formate respostas com quebras de linha para facilitar leitura
- Use markdown para destacar informações importantes (**negrito**, listas)

`;

    // Adicionar contexto do dashboard
    if (context.dashboardData) {
      systemPrompt += `\n📊 DADOS ATUAIS DO DASHBOARD:
- Total de alunos: ${context.dashboardData.totalAlunos}
- Turmas ativas: ${context.dashboardData.turmasAtivas}
- Atividades criadas: ${context.dashboardData.atividadesCriadas}
- Taxa de conclusão geral: ${context.dashboardData.taxaConclusao}%
- Alunos que precisam atenção: ${context.dashboardData.alunosRisco}
`;

      if (context.dashboardData.atividadesAtrasadas?.length > 0) {
        systemPrompt += `\nATIVIDADES ATRASADAS:\n`;
        context.dashboardData.atividadesAtrasadas.forEach((atv: any) => {
          systemPrompt += `- "${atv.nome}" (${atv.turma}): ${atv.conclusao} concluído\n`;
        });
      }
    }

    // Adicionar dados de alunos em risco
    if (context.alunosRisco && context.alunosRisco.length > 0) {
      systemPrompt += `\n🚨 ALUNOS EM RISCO:\n`;
      context.alunosRisco.forEach((aluno: any) => {
        systemPrompt += `- ${aluno.nome} (${aluno.turma}): ${aluno.pendentes} atividades pendentes, ${aluno.taxaConclusao}% conclusão\n`;
      });
    }

    // Adicionar dados de turmas
    if (context.turmasData && context.turmasData.length > 0) {
      systemPrompt += `\n📚 TURMAS:\n`;
      context.turmasData.forEach((turma: any) => {
        systemPrompt += `- ${turma.nome} (${turma.disciplina}): ${turma.totalAlunos} alunos, ${turma.taxaConclusao}% conclusão\n`;
      });
    }

    // Adicionar conteúdo de PDF
    if (context.pdfContent) {
      systemPrompt += `\n📄 CONTEÚDO DO PDF ENVIADO PELO PROFESSOR:\n${context.pdfContent}\n`;
    }

    return systemPrompt;
  }

  // ===== GERAR RESPOSTA =====
  async generateResponse(
    userMessage: string,
    context: GeminiContext
  ): Promise<string> {
    try {
      const systemPrompt = this.buildSystemPrompt(context);

      // Construir histórico de conversa
      const history = context.conversationHistory || [];
      const messages = [
        { role: "user", parts: [{ text: systemPrompt }] },
        {
          role: "model",
          parts: [
            {
              text: "Entendido! Estou pronto para ajudar com os dados fornecidos.",
            },
          ],
        },
        ...history.map((msg) => ({
          role: msg.role === "user" ? "user" : "model",
          parts: [{ text: msg.content }],
        })),
        { role: "user", parts: [{ text: userMessage }] },
      ];

      const chat = this.model.startChat({ history: messages.slice(0, -1) });
      const result = await chat.sendMessage(userMessage);
      const response = result.response;
      const text = response.text();

      return text;
    } catch (error) {
      console.error("Erro ao chamar Gemini:", error);
      throw new Error("Erro ao processar resposta da IA");
    }
  }

  // ===== ANALISAR PDF COM GEMINI =====
  async analyzePDF(pdfContent: string, fileName: string): Promise<string> {
    try {
      const prompt = `Você recebeu um documento PDF chamado "${fileName}". Analise o conteúdo abaixo e forneça:

1. **Tipo de documento** (plano de aula, prova, lista de exercícios, etc.)
2. **Tópicos principais** identificados
3. **Público-alvo** (série/ano recomendado)
4. **Nível de dificuldade** (fácil, médio, difícil)
5. **Sugestões de melhoria** práticas e acionáveis
6. Se for uma prova/exercícios, extraia as questões principais

CONTEÚDO DO PDF:
${pdfContent.substring(0, 8000)}

Responda de forma estruturada e profissional.`;

      const result = await this.model.generateContent(prompt);
      const response = result.response;
      return response.text();
    } catch (error) {
      console.error("Erro ao analisar PDF:", error);
      throw new Error("Erro ao analisar documento");
    }
  }

  // ===== GERAR ATIVIDADE PERSONALIZADA =====
  async generateActivity(
    topic: string,
    targetClass: string,
    difficulty: string,
    context: GeminiContext
  ): Promise<string> {
    try {
      const prompt = `Crie uma atividade de matemática com as seguintes especificações:

📚 **Tópico:** ${topic}
🎯 **Turma:** ${targetClass}
⭐ **Nível:** ${difficulty}

${
  context.dashboardData
    ? `\n**Contexto:** A turma ${targetClass} tem ${context.dashboardData.totalAlunos} alunos e uma taxa de conclusão de ${context.dashboardData.taxaConclusao}%.\n`
    : ""
}

**ESTRUTURA DA ATIVIDADE:**

1. **Título** (criativo e atrativo)
2. **Objetivo de aprendizagem** (baseado na BNCC)
3. **Duração estimada**
4. **Materiais necessários**
5. **Questões** (mínimo 6, organizadas por dificuldade crescente):
   - 2 questões fáceis (fixação de conceitos)
   - 2 questões médias (aplicação)
   - 2 questões desafiadoras (raciocínio avançado)
6. **Critérios de avaliação**
7. **Dicas para o professor**

Seja criativo, use exemplos do cotidiano dos alunos e inclua questões que estimulem o pensamento crítico!`;

      const result = await this.model.generateContent(prompt);
      const response = result.response;
      return response.text();
    } catch (error) {
      console.error("Erro ao gerar atividade:", error);
      throw new Error("Erro ao gerar atividade");
    }
  }

  // ===== GERAR PLANO DE AULA =====
  async generateLessonPlan(
    topic: string,
    targetClass: string,
    duration: number,
    context: GeminiContext
  ): Promise<string> {
    try {
      const prompt = `Crie um plano de aula completo de matemática:

📚 **Tema:** ${topic}
🎯 **Turma:** ${targetClass}
⏱️ **Duração:** ${duration} minutos

${
  context.turmasData
    ? `\n**Contexto da turma:** ${JSON.stringify(context.turmasData)}\n`
    : ""
}

**ESTRUTURA DO PLANO:**

1. **Objetivos de Aprendizagem** (alinhados à BNCC)
2. **Habilidades desenvolvidas**
3. **Materiais necessários**
4. **Introdução** (5-10 min)
   - Mobilização inicial
   - Questão problematizadora
5. **Desenvolvimento** (20-30 min)
   - Explicação do conteúdo
   - Exemplos práticos
   - Atividades em grupo/individual
6. **Consolidação** (10-15 min)
   - Exercícios de fixação
   - Discussão coletiva
7. **Avaliação**
   - Como verificar a aprendizagem
8. **Recursos adicionais**
   - Vídeos, sites, materiais complementares

Seja prático e criativo! Inclua metodologias ativas quando possível.`;

      const result = await this.model.generateContent(prompt);
      const response = result.response;
      return response.text();
    } catch (error) {
      console.error("Erro ao gerar plano de aula:", error);
      throw new Error("Erro ao gerar plano de aula");
    }
  }

  // ===== ANALISAR DESEMPENHO E DAR SUGESTÕES =====
  async analyzePerformance(context: GeminiContext): Promise<string> {
    try {
      const prompt = `Analise os dados de desempenho fornecidos e dê recomendações pedagógicas específicas e acionáveis.

**FOQUE EM:**
1. Identificar padrões e tendências
2. Priorizar ações por urgência e impacto
3. Sugerir estratégias diferenciadas para alunos com dificuldade
4. Propor ajustes em atividades com baixo engajamento
5. Recomendar recursos e metodologias adequadas

**FORMATO DA RESPOSTA:**
- Seja direto e objetivo
- Use listas quando apropriado
- Priorize ações (urgente, importante, desejável)
- Dê exemplos concretos quando possível

Baseie-se exclusivamente nos dados fornecidos no contexto.`;

      const result = await this.model.generateContent(prompt);
      const response = result.response;
      return response.text();
    } catch (error) {
      console.error("Erro ao analisar desempenho:", error);
      throw new Error("Erro ao analisar desempenho");
    }
  }
}

export default GeminiService;
